import React, { useEffect, useRef, useState } from "react";
import { Popover, Whisper } from "rsuite";
import { findWhere } from "underscore";
import { useAtomValue } from "jotai";
import { Button } from "reactstrap";
import pluralize from "pluralize";

import { GetDefaultLayout, ModuleLayoutSelector } from "./ModuleLayoutSelector";
import BrieRecordAssist from "../../../views/brie/BrieAssist/BrieRecordAssist";
import useCreateRecord from "../../../api/api-hooks/records/useCreateRecord";
import CreateRecordRendererV2 from "../../Records/CreateRecordRendererV2";
import DropdownOptionWithIcon from "../../Menus/DropdownOptionWithIcon";
import useCloudPermissions from "../../../hooks/useCloudPermissions";
import PopoverDropdownMenu from "../../Menus/PopoverDropdownMenu";
import { generatePrefill } from "../../../utils/QuickCreateUtils";
import { PrefillPair } from "../../../types/GlobalInterfaces";
import useUser from "../../../api/api-hooks/auth/useUser";
import InstanceUtils from "../../../utils/InstanceUtils";
import AppSpinner from "../../../elements/AppSpinner";
import FieldUtils from "../../../utils/FieldUtils";
import useModule from "../../../hooks/useModule";
import StrUtils from "../../../utils/StrUtils";
import { IsMobileAtom } from "../../../App";
import AppDrawer from "../AppDrawer";

// Handle updating Brie when using CreateRecordRendererV2
export function HandleUpdateBrie(
  result: any,
  constructedPrefill: PrefillPair[],
  setConstructedPrefill: React.Dispatch<React.SetStateAction<PrefillPair[]>>,
  values: any,
  setValues: any
) {
  const newPayload: any = {};
  const newPrefill: any = [];

  result?.values?.forEach((item: any) => {
    newPayload[item?.fieldAPIName] = item?.value;
    newPrefill.push({ Field: item?.fieldAPIName, Value: item?.value });
  });

  if (values && Object.keys(values).length) {
    for (const key of Object.keys(values)) {
      if (!!findWhere(newPrefill, { Field: key })) continue;

      newPrefill.push({ Field: key, Value: values[key] });
    }
  }

  if (constructedPrefill?.length) {
    for (const item of constructedPrefill) {
      if (!!findWhere(newPrefill, { Field: item?.Field })) continue;

      newPrefill.push({ Field: item?.Field, Value: item?.Value });
    }
  }

  setValues((prev: any) => ({ ...(prev || []), ...newPayload }));
  setConstructedPrefill(newPrefill);
}

interface Props {
  open: boolean;
  onCreate: any;
  onCreateForm: any;
  moduleAPIName: string;
  prefill: any;
  originalRecord?: any;
  defaultRecordLayout?: string;
  loading?: boolean;
  onClose: () => void;
  isDuplicate?: boolean;
}

/**
 * CreateRecordDrawer - Drawer used for creating records in various contexts.
 *
 * @param {boolean} open - Whether the drawer is open or not.
 * @param {function} onClose - Function called when the drawer is closed.
 * @param {function} onCreate - Function called when the user presses "Create" button
 * @param {function} onCreateForm - Function called when the user presses "Create From" button
 * @param {string} moduleAPIName - The module the record should be created in.
 * @param {object} prefill - Prefill object to prefill as many form inputs as valid keys.
 * @param {object} originalRecord - A parent or associated record you want to append to variableValues.
 */
const CreateRecordDrawer = ({
  open,
  onClose,
  onCreate,
  onCreateForm,
  moduleAPIName,
  prefill = null,
  originalRecord = null,
  defaultRecordLayout,
  loading,
  isDuplicate = false,
}: Props) => {
  const isMobile = useAtomValue(IsMobileAtom);
  const { module } = useModule(moduleAPIName, true, false, false);
  const [values, setValues] = useState(null);
  const [variableValues, setVariableValues] = useState<any>({});
  const [loadingPayload, setLoadingPayload] = useState(false);
  const [constructedPrefill, setConstructedPrefill] = useState<PrefillPair[]>(
    []
  );
  const { user, userLoading } = useUser();
  const { createRecord, recordCreating, createRecordError } = useCreateRecord();
  const { cPermissions } = useCloudPermissions();
  const [showError, setShowError] = useState(false);
  const [error, setError] = useState("");
  const [layout, setLayout] = useState(
    module ? GetDefaultLayout(module, defaultRecordLayout) || null : null
  );
  const recordRef = useRef();
  recordRef.current = originalRecord;

  useEffect(() => {
    if (!module) return;

    let matchedLayout = GetDefaultLayout(module, defaultRecordLayout);

    if (matchedLayout) setLayout(matchedLayout);
  }, [module, defaultRecordLayout]);

  useEffect(() => {
    if (userLoading) return;

    let variableVals: any = {
      user: {
        id: user?.id,
        _RecordName: user?.Name,
        FirstName: StrUtils.getFirstName(user?.Name),
        LastName: StrUtils.getLastName(user?.Name),
        /** For Feedback, to know who send the message. */
        _ModuleAPIName: moduleAPIName === "Feedback" ? "People" : "Contacts",
      },
    };

    if (recordRef.current) {
      variableVals.record = structuredClone(recordRef.current);
    }

    setVariableValues(variableVals);

    generatePrefill(
      prefill,
      variableVals,
      module?.Fields,
      setConstructedPrefill
    );
  }, [prefill, module?.Fields, user?.id, user?.Name, userLoading]);

  return (
    <AppDrawer
      size="md"
      open={open}
      onClose={onClose}
      backdrop={"static"}
      keyboard={false}
      headerTitle={` 
          ${
            module?.Name
              ? " New " + pluralize.singular(module?.Name)
              : " New Record"
          }`}
      headerOptions={
        <div className="d-flex align-items-center">
          <ModuleLayoutSelector
            module={module as any}
            layout={layout}
            setLayout={setLayout}
            style={{ width: 150 }}
            className="mr-3"
            hideOnOne
          />
          {module && (
            <BrieRecordAssist
              module={module}
              onConfirm={(result) =>
                HandleUpdateBrie(
                  result,
                  constructedPrefill,
                  setConstructedPrefill,
                  values,
                  setValues
                )
              }
            />
          )}
          <Button
            size="sm"
            color="primary"
            disabled={recordCreating || loadingPayload}
            onClick={handleCreateRecord}
            style={{ width: isMobile ? undefined : 90 }}
          >
            {recordCreating || loadingPayload ? (
              <AppSpinner size="sm" color="white" />
            ) : (
              "Create"
            )}
          </Button>
          {cPermissions.cloud_create_credential && !isMobile && (
            <Button
              size="sm"
              color="secondary"
              outline
              className="btn-icon-only"
            >
              <Whisper
                speaker={
                  <Popover className="sidebar-popover" full>
                    <PopoverDropdownMenu>
                      <DropdownOptionWithIcon
                        name="Create Public Form"
                        icon="mdi mdi-form-select"
                        onClick={onCreateForm}
                      />
                    </PopoverDropdownMenu>
                  </Popover>
                }
                placement="auto"
                trigger="click"
              >
                <i
                  className="mdi mdi-share-variant"
                  style={{
                    fontSize: "18px",
                  }}
                ></i>
              </Whisper>
            </Button>
          )}
        </div>
      }
      bodyCSS="px-0 pt-0 create-drawer-body pb-8"
      error={
        createRecordError
          ? showError
            ? {
                message: createRecordError,
                onClear: () => setShowError(false),
              }
            : error
            ? {
                message: error,
                onClear: () => setError(""),
              }
            : undefined
          : error
          ? {
              message: error,
              onClear: () => setError(""),
            }
          : undefined
      }
      body={
        loading ? (
          <div className="w-100 d-flex justify-content-center mt-6">
            <AppSpinner size="lg" />
          </div>
        ) : (
          <CreateRecordRendererV2
            module={module}
            prefill={constructedPrefill}
            variableValues={variableValues}
            layout={layout}
            onChange={onChange}
          />
        )
      }
    />
  );

  async function handleCreateRecord() {
    if (!module) return;

    setLoadingPayload(true);
    setError("");

    const missingFields = [];

    for (const f of module?.Fields || []) {
      if (!f?.Required) continue;

      if (f?.FieldType === "Subitem" || f?.FieldType === "MultiSubitem") {
        for (const sF of f?.SubitemFields || []) {
          if (!sF?.Required) continue;

          if (f?.FieldType === "Subitem") {
            if (
              !FieldUtils.objHasValueAtKey(values?.[f?.APIName], sF?.APIName)
            ) {
              missingFields.push(f.Name);
              break;
            }
          }

          if (f?.FieldType === "MultiSubitem") {
            let missingRequiredSf = false;

            for (const val of values?.[f?.APIName] || []) {
              if (!FieldUtils.objHasValueAtKey(val, sF?.APIName)) {
                missingRequiredSf = true;
                missingFields.push(f.Name);
                break;
              }
            }

            if (missingRequiredSf) break;
          }
        }
      }

      if (!FieldUtils.objHasValueAtKey(values, f?.APIName)) {
        missingFields.push(f.Name);
      }
    }

    if (missingFields.length) {
      setLoadingPayload(false);
      return setError(
        `Please fill in the following required fields: ${missingFields.join(
          ", "
        )}`
      );
    }

    let formattedPayload;
    try {
      formattedPayload = await InstanceUtils.formatRecordCreationPayload(
        module?.Fields,
        values,
        isDuplicate
      );
    } catch (error) {
      setLoadingPayload(false);

      setError("We were unable to create the record. Please Try again.");
    }

    if (!formattedPayload) return;

    createRecord({
      module: module,
      payload: formattedPayload,
      onCreate: onCreate,
      onSettled: () => {
        setLoadingPayload(false);
      },
      onError: (e: any) => {
        console.log("error", e);

        setError("We were unable to create the record. Please Try again.");
      },
    });

    setShowError(true);
  }

  // Handles the change of values in CreateRecordRenderer
  function onChange(values: any) {
    setShowError(false);
    setValues(values);
  }
};

export default React.memo(CreateRecordDrawer);
